import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import type {
  BlitzschutzStand, BsAufbau, BsSpdZeile, BsStange, BsVergleich, BsVorgabe,
} from './elektro/blitzschutz';
import { ANNAHMEN_HINWEIS, PDF_HINWEIS, PLAN_PDF_HINWEIS, zerlegeBefund } from './elektro/blitzschutz';
import { PageHeader } from '../rahmen/PageHeader';
import './elektro/blitzschutz.css';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe, LoadingState, StatusBadge,
  type Spalte, type Zustand,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * Blitzschutz, Erdung und Überspannung (W2-C1/W2-C2, 16.10.2026) — Abschnitt
 * „Blitzschutz und Erdung".
 *
 * Die Seite LIEST `GET …/elektro/blitzschutz` und den Plan `…/plan.svg`.
 * Gerechnet wird auf dem Server (Blitzkugelverfahren, Trennungsabstand,
 * Ableitungen, Erder, SPD-Kaskade, Potentialausgleich), die Seite schreibt nichts.
 *
 * Drei PDF-Knöpfe (ELT-BLZ, ELT-SPD, ELT-BLP) und die Excel-Mappe. Nachweis und Überspannung legen
 * nur bei geändertem Stand eine neue Berichtsfassung an, sonst die abgelegte (F11); der Plan zeichnet mit
 * Einfang (TX-STANDARD S3.6) und legt weiter je Abruf eine neue an (R25) — das steht am Knopf.
 */
export function ElektroBlitzschutz() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <LoadingState />;
  return <BlitzschutzAnsicht api={api} projektId={projekt.id} />;
}

function zustandVon(status: string): Zustand {
  return status === 'fehler' ? 'fehler' : status === 'warnung' ? 'warnung' : status === 'ok' ? 'gut' : 'info';
}

function Befundliste({ befunde }: { befunde: string[] }) {
  if (befunde.length === 0) return null;
  return (
    <ul className="liste">
      {befunde.map((b, i) => {
        const { stufe, text } = zerlegeBefund(b);
        const z: Zustand = stufe === 'fehler' ? 'fehler' : stufe === 'warnung' ? 'warnung' : 'info';
        return <li key={`${i}-${text.slice(0, 24)}`}><StatusBadge zustand={z} text={text} /></li>;
      })}
    </ul>
  );
}

function zahl(w: number | null | undefined, nachkomma = 1): string {
  return w === null || w === undefined ? '—' : w.toLocaleString('de-DE', { maximumFractionDigits: nachkomma });
}

export function BlitzschutzAnsicht({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [stand, setStand] = useState<BlitzschutzStand | null>(null);
  const [plan, setPlan] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    api.elektroBlitzschutz(projektId)
      .then((s) => { if (aktiv) { setStand(s); setFehler(null); } })
      .catch((e: unknown) => { if (aktiv) setFehler(e instanceof Error ? e.message : String(e)); });
    return () => { aktiv = false; };
  }, [api, projektId]);

  async function planZeigen() {
    setLaeuft('plan');
    try {
      setPlan(await api.elektroBlitzschutzPlanSvg(projektId));
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  }

  async function laden(art: 'nachweis' | 'ueberspannung' | 'plan' | 'excel') {
    setLaeuft(art);
    try {
      if (art === 'nachweis') await api.elektroBlitzschutzNachweisLaden(projektId);
      else if (art === 'ueberspannung') await api.elektroBlitzschutzUeberspannungLaden(projektId);
      else if (art === 'plan') await api.elektroBlitzschutzPlanLaden(projektId);
      else await api.elektroBlitzschutzListen(projektId);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  }

  const aktionen = (
    <>
      <button type="button" className="m-knopf" disabled={laeuft !== null}
              onClick={() => { void laden('excel'); }}>
        Excel-Mappe
      </button>
      <button type="button" className="m-knopf" disabled={laeuft !== null} title={PDF_HINWEIS}
              onClick={() => { void laden('nachweis'); }}>
        Blitzschutz und Erdung als PDF
      </button>
      <button type="button" className="m-knopf" disabled={laeuft !== null} title={PDF_HINWEIS}
              onClick={() => { void laden('ueberspannung'); }}>
        Überspannung und PA als PDF
      </button>
      <button type="button" className="m-knopf" disabled={laeuft !== null} title={PLAN_PDF_HINWEIS}
              onClick={() => { void laden('plan'); }}>
        Plan als PDF (neue Fassung)
      </button>
    </>
  );

  return (
    <div className="seite">
      <PageHeader
        titel="Blitzschutz, Erdung und Überspannung"
        beschreibung="Risikoanalyse (Übernahme und Gegenrechnung), Blitzkugelverfahren an den Fangstangen, Trennungsabstand, Ableitungen, Fundament- und Ringerder, SPD-Kaskade je Verteiler und Potentialausgleich. Gerechnet beim Abruf, nur lesend."
        aktionen={aktionen}
      />
      <p className="hinweis">{PDF_HINWEIS} {PLAN_PDF_HINWEIS}</p>
      {fehler && <ErrorState titel="Blitzschutz und Überspannung konnten nicht geladen werden." technisch={fehler} />}
      {!fehler && !stand && <LoadingState />}
      {stand && stand.kennzahlen.fangstangen === 0 && stand.ueberspannung.zeilen.length === 0 && (
        <EmptyState titel="Keine Fangstangen und keine Verteiler im Modell"
                    erklaerung="Fangstangen und Verteiler kommen aus der Elektroaufnahme." />
      )}
      {stand && (stand.kennzahlen.fangstangen > 0 || stand.ueberspannung.zeilen.length > 0) && (
        <Inhalt stand={stand} plan={plan} planZeigen={() => { void planZeigen(); }} planLaeuft={laeuft === 'plan'} />
      )}
    </div>
  );
}

function Inhalt({ stand, plan, planZeigen, planLaeuft }: {
  stand: BlitzschutzStand; plan: string | null; planZeigen: () => void; planLaeuft: boolean;
}) {
  const k = stand.kennzahlen;
  return (
    <>
      {k.aufAnnahmen && <p className="hinweis hinweis-warnung">{ANNAHMEN_HINWEIS}</p>}
      <KpiReihe>
        <KpiCard titel="Blitzschutzklasse" wert={stand.klasse?.name ?? '?'}
                 vergleich={stand.klasse ? `Blitzkugel r = ${stand.klasse.kugelradiusM} m` : 'Klasse fehlt'} />
        <KpiCard titel="Fangstangen" wert={k.fangstangen} />
        <KpiCard titel="Dachaufbauten" wert={k.dachaufbauten} vergleich={`${k.geschuetzt} im Schutzraum`} />
        <KpiCard titel="nicht geschützt" wert={k.ungeschuetzt} zustand={k.ungeschuetzt > 0 ? 'warnung' : 'gut'}
                 zustandText={k.ungeschuetzt > 0 ? 'prüfen' : 'keine'} />
        <KpiCard titel="Ableitungen erforderlich" wert={k.ableitungenErforderlich}
                 vergleich={`${stand.ableitung.durchgehendModelliert} durchgehend modelliert`} />
        <KpiCard titel="Verteiler ohne SPD" wert={k.verteilerOhneSpd} zustand={k.verteilerOhneSpd > 0 ? 'warnung' : 'gut'}
                 zustandText={k.verteilerOhneSpd > 0 ? 'prüfen' : 'keine'} />
        <KpiCard titel="Warnungen" wert={k.warnungen} zustand={k.warnungen > 0 ? 'warnung' : 'gut'}
                 zustandText={k.warnungen > 0 ? 'prüfen' : 'keine'} />
        <KpiCard titel="Fehler" wert={k.fehler} zustand={k.fehler > 0 ? 'fehler' : 'gut'}
                 zustandText={k.fehler > 0 ? 'beheben' : 'keine'} />
      </KpiReihe>
      <Risiko stand={stand} />
      <ContentSection titel="Plan der Fangeinrichtung"
                      beschreibung="Dachaufsicht mit Fangstangen, Schutzkreisen der Einzelstangen am Dach und den Dachaufbauten (rot: nicht im Schutzraum). Pfeil zeigt +Y, nicht Norden.">
        {!plan && (
          <button type="button" className="m-knopf" disabled={planLaeuft} onClick={planZeigen}>
            Plan anzeigen
          </button>
        )}
        {plan && <div className="bs-plan" data-testid="blitzschutz-plan" dangerouslySetInnerHTML={{ __html: plan }} />}
      </ContentSection>
      <Aufbauten stand={stand} />
      <Stangen zeilen={stand.stangen} />
      <Ableitung stand={stand} />
      <Erdung stand={stand} />
      <Ueberspannung stand={stand} />
      <Potentialausgleich stand={stand} />
      <Vorgaben zeilen={stand.vorgaben} />
      {stand.befunde.length > 0 && (
        <ContentSection titel="Alle Befunde">
          <Befundliste befunde={stand.befunde} />
        </ContentSection>
      )}
    </>
  );
}

function Risiko({ stand }: { stand: BlitzschutzStand }) {
  const spalten: Spalte<BsVergleich>[] = [
    { schluessel: 'groesse', titel: 'Größe', wert: (z) => z.groesse },
    { schluessel: 'einheit', titel: 'Einheit', wert: (z) => z.einheit },
    { schluessel: 'dokument', titel: 'Analyse', art: 'zahl', nachkomma: 4, wert: (z) => z.dokument ?? '' },
    { schluessel: 'gerechnet', titel: 'Gegenrechnung', art: 'zahl', nachkomma: 4, wert: (z) => z.gerechnet },
    { schluessel: 'abw', titel: 'Abweichung', einheit: '%', art: 'zahl', nachkomma: 2, wert: (z) => z.abweichungProzent ?? '' },
    {
      schluessel: 'status', titel: 'Status', wert: (z) => z.status,
      inhalt: (z) => <StatusBadge zustand={zustandVon(z.status)} text={z.status} klein />,
    },
  ];
  return (
    <ContentSection titel="Risikoanalyse nach DIN EN 62305-2"
                    beschreibung="Übernahme der vorliegenden Analyse; Einfangflächen und Einschlaghäufigkeiten sind mit den Formeln aus Anhang A nachgerechnet.">
      <DataTable name="Gegenrechnung" spalten={spalten} zeilen={stand.risiko.vergleiche} schluessel={(z) => z.groesse} dicht />
      <p className="klein">R1 (geschützt) ≤ R_T: <strong>{stand.risiko.risikoUnterRt ? 'ja' : 'nein'}</strong></p>
      <Befundliste befunde={stand.risiko.befunde} />
    </ContentSection>
  );
}

function Aufbauten({ stand }: { stand: BlitzschutzStand }) {
  const [alle, setAlle] = useState(false);
  const zeilen = alle ? stand.dachaufbauten : stand.dachaufbauten.filter((a) => !a.geschuetzt);
  const spalten: Spalte<BsAufbau>[] = [
    { schluessel: 'kennung', titel: 'Bauteil', wert: (z) => z.kennung },
    { schluessel: 'art', titel: 'Art', wert: (z) => z.art },
    { schluessel: 'x', titel: 'x', einheit: 'm', art: 'zahl', nachkomma: 1, wert: (z) => z.x },
    { schluessel: 'y', titel: 'y', einheit: 'm', art: 'zahl', nachkomma: 1, wert: (z) => z.y },
    { schluessel: 'z', titel: 'Oberkante', einheit: 'm', art: 'zahl', nachkomma: 2, wert: (z) => z.maxZ },
    { schluessel: 'h', titel: 'Schutzhöhe', einheit: 'm', art: 'zahl', nachkomma: 2, wert: (z) => z.schutzhoehe ?? '' },
    { schluessel: 'r', titel: 'Reserve', einheit: 'm', art: 'zahl', nachkomma: 2, wert: (z) => z.reserveM ?? '' },
    {
      schluessel: 'g', titel: 'geschützt', wert: (z) => (z.geschuetzt ? 1 : 0),
      inhalt: (z) => <StatusBadge zustand={z.geschuetzt ? 'gut' : 'warnung'} text={z.geschuetzt ? 'ja' : 'nein'} klein />,
    },
    { schluessel: 'stange', titel: 'nächste Stange', wert: (z) => z.naechsteStange ?? '' },
  ];
  return (
    <ContentSection titel="Blitzkugelverfahren — Dachaufbauten"
                    beschreibung="Geprüft werden die oberen Ecken und der Mittelpunkt jedes Dachaufbaus gegen die Schutzhöhe der rollenden Kugel. Das Metalldach gilt als Teil der Fangeinrichtung.">
      <ul className="liste klein">
        {stand.dachaufbautenJeArt.map((a) => (
          <li key={a.art}><strong>{a.art}</strong>: {a.anzahl}{a.ungeschuetzt > 0 ? ` · ${a.ungeschuetzt} nicht geschützt` : ''}</li>
        ))}
      </ul>
      <label className="klein">
        <input type="checkbox" checked={alle} onChange={(e) => setAlle(e.target.checked)} /> auch geschützte zeigen
      </label>
      <DataTable name={`${alle ? 'Alle Dachaufbauten' : 'Nicht geschützt'} (${zeilen.length})`} spalten={spalten}
                 zeilen={zeilen} schluessel={(z) => `${z.kennung}-${z.x}-${z.y}`} dicht />
    </ContentSection>
  );
}

function Stangen({ zeilen }: { zeilen: BsStange[] }) {
  const spalten: Spalte<BsStange>[] = [
    { schluessel: 'k', titel: 'Stange', wert: (z) => z.kennung },
    { schluessel: 't', titel: 'Typ', wert: (z) => z.typ ?? '' },
    { schluessel: 'x', titel: 'x', einheit: 'm', art: 'zahl', nachkomma: 1, wert: (z) => z.x },
    { schluessel: 'y', titel: 'y', einheit: 'm', art: 'zahl', nachkomma: 1, wert: (z) => z.y },
    { schluessel: 'h', titel: 'h', einheit: 'm', art: 'zahl', nachkomma: 2, wert: (z) => z.hoeheM },
    { schluessel: 'rs', titel: 'r_s am Dach', einheit: 'm', art: 'zahl', nachkomma: 1, wert: (z) => z.schutzradiusDachM },
    { schluessel: 's', titel: 'Trennungsabstand s', einheit: 'm', art: 'zahl', nachkomma: 2, wert: (z) => z.trennungsabstandM ?? '' },
    { schluessel: 'a', titel: 'Abstand Aufbau', einheit: 'm', art: 'zahl', nachkomma: 1, wert: (z) => z.abstandObjektM ?? '' },
    {
      schluessel: 'ok', titel: 's eingehalten', wert: (z) => (z.trennungOk ? 1 : 0),
      inhalt: (z) => <StatusBadge zustand={z.trennungOk ? 'gut' : 'warnung'} text={z.trennungOk ? 'ja' : 'nein'} klein />,
    },
  ];
  return (
    <ContentSection titel="Fangstangen und Trennungsabstand"
                    beschreibung="s = k_i · k_c / k_m · l mit l = Stangenhöhe über dem Metalldach (Annahme); verglichen mit dem Abstand zum nächsten Dachaufbau.">
      <DataTable name={`Fangstangen (${zeilen.length})`} spalten={spalten} zeilen={zeilen} schluessel={(z) => z.kennung} dicht />
    </ContentSection>
  );
}

function Ableitung({ stand }: { stand: BlitzschutzStand }) {
  const a = stand.ableitung;
  return (
    <ContentSection titel="Ableitungen" beschreibung="Anzahl nach Umfang und typischem Abstand der Klasse (DIN EN 62305-3 Tab. 4).">
      <dl className="bs-werte">
        <div><dt>Umfang (Hülle Ringerder)</dt><dd>{zahl(a.umfangM, 0)} m</dd></div>
        <div><dt>typischer Abstand</dt><dd>{zahl(a.abstandSollM, 0)} m</dd></div>
        <div><dt>erforderlich</dt><dd>{a.erforderlich}</dd></div>
        <div><dt>durchgehend modelliert</dt><dd>{a.durchgehendModelliert}</dd></div>
        <div><dt>senkrecht nur im Dachbereich</dt><dd>{a.nurDach}</dd></div>
        <div><dt>Wandanschlusspunkte (Bodennähe)</dt><dd>{a.wandanschlusspunkte} ({a.wandanschlusspunkteBoden})</dd></div>
        <div><dt>Anschlussfahnen / Erdungsfestpunkte</dt><dd>{a.anschlussfahnen} / {a.erdungsfestpunkte}</dd></div>
        <div><dt>natürliche Ableitungen</dt><dd>{a.natuerlicheAbleitungen}</dd></div>
      </dl>
      <Befundliste befunde={a.befunde} />
    </ContentSection>
  );
}

function Erdung({ stand }: { stand: BlitzschutzStand }) {
  const e = stand.erdung;
  return (
    <ContentSection titel="Erdungsanlage" beschreibung="Fundament- und Ringerder nach DIN 18014, Erder Typ B nach DIN EN 62305-3 (r_e ≥ l_1), größtes erderfreies Feld als Abschätzung der Maschenweite.">
      <dl className="bs-werte">
        <div><dt>Fundamenterder</dt><dd>{zahl(e.fundamenterderM, 0)} m</dd></div>
        <div><dt>Ringerder</dt><dd>{zahl(e.ringerderM, 0)} m</dd></div>
        <div><dt>Umfang / Fläche der Hülle</dt><dd>{zahl(e.umfangM, 0)} m / {zahl(e.flaecheM2, 0)} m²</dd></div>
        <div><dt>r_e / l_1</dt><dd>{zahl(e.mittlererRadiusM)} m / {zahl(e.l1M)} m</dd></div>
        <div><dt>Typ-B-Bedingung</dt><dd>{e.typBErfuellt === null ? 'offen' : e.typBErfuellt ? 'erfüllt' : 'nicht erfüllt'}</dd></div>
        <div><dt>größtes erderfreies Feld</dt><dd>{zahl(e.groessteMaschenweiteM)} m (zulässig {zahl(e.maschenweiteZulM, 0)} m)</dd></div>
      </dl>
      <ul className="liste klein" data-testid="erderhoehen">
        {stand.erderhoehen.map((h) => (
          <li key={h.art}>
            <strong>{h.art}</strong>: {h.gezaehlt} Achsen gezählt ({zahl(h.gezaehltModellM, 0)} m Modell, {zahl(h.gezaehltGrundrissM, 0)} m
            Grundriss){h.quer > 0 ? `, ${h.quer} quer (Anschluss, ${zahl(h.querM, 0)} m)` : ''}
            {h.darueber > 0 ? `, ${h.darueber} oberhalb der Erdungsebene (${zahl(h.darueberM, 0)} m, z ${zahl(h.darueberZMinM)} … ${zahl(h.darueberZMaxM)} m)` : ''}
          </li>
        ))}
      </ul>
      <Befundliste befunde={e.befunde} />
    </ContentSection>
  );
}

function Ueberspannung({ stand }: { stand: BlitzschutzStand }) {
  const u = stand.ueberspannung;
  const a = u.anforderungen;
  const spalten: Spalte<BsSpdZeile>[] = [
    { schluessel: 'k', titel: 'Verteiler', wert: (z) => z.kennzeichen },
    { schluessel: 'art', titel: 'Art', wert: (z) => z.art },
    { schluessel: 'v', titel: 'Versorgung', wert: (z) => z.versorgungsart },
    { schluessel: 'o', titel: 'vorgeschaltet', wert: (z) => z.ober ?? '' },
    { schluessel: 'ist', titel: 'Ist', wert: (z) => z.ist ?? '—' },
    { schluessel: 'soll', titel: 'Soll', wert: (z) => z.soll ?? '' },
    { schluessel: 'l', titel: 'Zuleitung', einheit: 'm', art: 'zahl', nachkomma: 0, wert: (z) => z.zuleitungLaengeM ?? '' },
    {
      schluessel: 's', titel: 'Status', wert: (z) => z.status,
      inhalt: (z) => <StatusBadge zustand={zustandVon(z.status)} text={z.status} klein />,
    },
  ];
  return (
    <ContentSection titel="Überspannungsschutz"
                    beschreibung="Pflicht nach DIN VDE 0100-443/-534, SPD-Kaskade Typ 1 (NSHV) und Typ 2 (UV, IT-Verteiler); Anforderungen je Pol nach DIN EN 62305-1 Anhang E.">
      <ul className="liste klein">{u.pflichtGruende.map((g) => <li key={g}>{g}</li>)}</ul>
      <dl className="bs-werte">
        <div><dt>I_imp L-N (Typ 1)</dt><dd>≥ {zahl(a.iimpLnKa)} kA</dd></div>
        <div><dt>I_imp N-PE (3+1)</dt><dd>≥ {zahl(a.iimpNpeKa)} kA</dd></div>
        {a.schutzpegel.map((p) => (
          <div key={p.kategorie}><dt>U_p,max Kategorie {p.kategorie}</dt><dd>{zahl(p.upMaxKv)} kV (U_w {zahl(p.uwKv)} kV)</dd></div>
        ))}
      </dl>
      <DataTable name={`Verteiler (${u.zeilen.filter((z) => z.soll !== null).length})`} spalten={spalten}
                 zeilen={u.zeilen.filter((z) => z.soll !== null)} schluessel={(z) => z.kennzeichen} dicht />
      <Befundliste befunde={u.befunde} />
    </ContentSection>
  );
}

function Potentialausgleich({ stand }: { stand: BlitzschutzStand }) {
  const p = stand.potentialausgleich;
  return (
    <ContentSection titel="Potentialausgleich und Netzform"
                    beschreibung="Hauptschutzpotentialausgleich nach DIN VDE 0100-540, Blitzschutz-Potentialausgleich nach DIN EN 62305-3 Tab. 8/9, zentraler Erdungspunkt bei TN-S mit mehreren Einspeisungen.">
      <dl className="bs-werte">
        <div><dt>Haupt-PA</dt><dd>{zahl(p.hauptPaMm2)} mm² Cu — {p.hauptPaHerleitung}</dd></div>
        <div><dt>PA-Schienen</dt><dd>{p.schienen} (ohne Verteiler {p.schienenOhneVerteiler}, ohne Raum {p.schienenOhneRaum})</dd></div>
      </dl>
      <ul className="liste klein">
        {p.gruppen.map((g) => (
          <li key={g.gruppe}>
            <StatusBadge zustand={zustandVon(g.status)} text={g.gruppe} klein /> {g.trafos} Trafo(s), {g.nshv.join(', ')} —
            ZEP {g.zepAngegeben ? 'angegeben' : 'nicht festgelegt'}
          </li>
        ))}
      </ul>
      <Befundliste befunde={p.befunde} />
    </ContentSection>
  );
}

function Vorgaben({ zeilen }: { zeilen: BsVorgabe[] }) {
  const spalten: Spalte<BsVorgabe>[] = [
    { schluessel: 'f', titel: 'Vorgabe', wert: (z) => z.feld },
    { schluessel: 'w', titel: 'Wert', wert: (z) => z.wert },
    { schluessel: 'e', titel: 'Einheit', wert: (z) => z.einheit },
    { schluessel: 'h', titel: 'Herkunft', wert: (z) => z.herkunft + (z.rueckfall ? ' (Rückfall)' : '') },
    {
      schluessel: 'b', titel: 'bestätigt', wert: (z) => (z.bestaetigt ? 1 : 0),
      inhalt: (z) => <StatusBadge zustand={z.bestaetigt ? 'gut' : 'warnung'} text={z.bestaetigt ? 'ja' : 'nein'} klein />,
    },
    { schluessel: 'n', titel: 'Normbezug', wert: (z) => z.normbezug },
  ];
  return (
    <ContentSection titel="Vorgaben" beschreibung="Alle Vorgaben entstehen unbestätigt (Migrationen 0990 und 1000).">
      <DataTable name={`Vorgaben (${zeilen.length})`} spalten={spalten} zeilen={zeilen} schluessel={(z) => z.feld} dicht />
    </ContentSection>
  );
}
